import {
  BRIDGE_CHANNEL,
  BRIDGE_VERSION,
  createGuestRequest,
  parseHostHandshake,
  parseGuestRequest,
  parseHostMessageEvent,
  parseHostResponseFor,
  type BridgeCommand,
  type BridgeError,
  type BridgeEvent,
  type EventPayload,
  type GuestRequest,
  type HostEvent,
  type HostMessage,
  type RequestPayload,
  type RequestResult,
  type StateStamp
} from '@oysterjs/smartfill-contracts';

const DEFAULT_REQUEST_TIMEOUT_MS = 15_000;

/** The Chrome Web Store build of Cara Smart Fill. The ingress's frame-ancestors pins the same ID. */
export const STORE_EXTENSION_ORIGIN = 'chrome-extension://epmjppbjijdocdaaabiaamkhmndboppj';

interface GuestWindow {
  readonly parent: WindowProxy;
  readonly location: Pick<Location, 'hostname'>;
  addEventListener(type: 'message', listener: (event: MessageEvent<unknown>) => void): void;
  removeEventListener(type: 'message', listener: (event: MessageEvent<unknown>) => void): void;
  setTimeout(handler: () => void, timeout: number): number;
  clearTimeout(handle: number): void;
}

interface ParentWindow {
  postMessage(message: unknown, targetOrigin: string): void;
}

export interface SmartFillBridgeClientOptions {
  guestWindow?: GuestWindow;
  parentWindow?: ParentWindow;
  requestTimeoutMs?: number;
}

interface PendingRequest {
  request: GuestRequest;
  sent: boolean;
  timer: number;
  resolve: (result: unknown) => void;
  reject: (error: Error) => void;
}

type EventListener = (payload: unknown, stamp?: StateStamp) => void;

export class SmartFillBridgeClient {
  private readonly guestWindow: GuestWindow;
  private readonly parentWindow: ParentWindow;
  private readonly requestTimeoutMs: number;
  private readonly pending = new Map<string, PendingRequest>();
  private readonly listeners = new Map<BridgeEvent, Set<EventListener>>();
  private pendingNavigation: EventPayload<'sidebar.navigate'> | null = null;
  private trustedOrigin: string | null = null;
  private hostInstanceId: string | null = null;
  private disconnected = false;
  private versionError: SmartFillBridgeRequestError | null = null;

  constructor(options: SmartFillBridgeClientOptions = {}) {
    this.guestWindow = options.guestWindow ?? window;
    this.parentWindow = options.parentWindow ?? this.guestWindow.parent;
    this.requestTimeoutMs = options.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
    this.guestWindow.addEventListener('message', this.handleMessage);
  }

  request<C extends BridgeCommand>(
    command: C,
    payload: RequestPayload<C>,
    options: { timeoutMs?: number } = {}
  ): Promise<RequestResult<C>> {
    if (this.disconnected) {
      return Promise.reject(new SmartFillBridgeDisconnectedError());
    }
    if (this.versionError) return Promise.reject(this.versionError);

    const request = parseGuestRequest(createGuestRequest(command, payload)) as GuestRequest<C>;
    const timeoutMs = options.timeoutMs ?? this.requestTimeoutMs;

    return new Promise<RequestResult<C>>((resolve, reject) => {
      const timer = this.guestWindow.setTimeout(() => {
        this.pending.delete(request.requestId);
        reject(
          new SmartFillBridgeRequestError({
            code: 'TIMEOUT',
            message: `${command} timed out waiting for the Cara extension.`,
            retryable: true
          })
        );
      }, timeoutMs);
      const pending: PendingRequest = {
        request: request as unknown as GuestRequest,
        sent: false,
        timer,
        resolve: (result) => resolve(result as RequestResult<C>),
        reject
      };
      this.pending.set(request.requestId, pending);
      this.sendPending(pending);
    });
  }

  subscribe<E extends BridgeEvent>(
    event: E,
    listener: (payload: EventPayload<E>, stamp?: StateStamp) => void
  ): () => void {
    if (this.disconnected) throw new SmartFillBridgeDisconnectedError();
    const listeners = this.listeners.get(event) ?? new Set<EventListener>();
    const eventListener: EventListener = (payload, stamp) =>
      listener(payload as EventPayload<E>, stamp);
    listeners.add(eventListener);
    this.listeners.set(event, listeners);
    if (event === 'sidebar.navigate' && this.pendingNavigation) {
      const payload = this.pendingNavigation;
      this.pendingNavigation = null;
      listener(payload as EventPayload<E>);
    }
    return () => {
      listeners.delete(eventListener);
      if (listeners.size === 0) this.listeners.delete(event);
    };
  }

  disconnect(): void {
    if (this.disconnected) return;
    this.disconnected = true;
    this.guestWindow.removeEventListener('message', this.handleMessage);
    this.rejectPending(new SmartFillBridgeDisconnectedError());
    this.listeners.clear();
    this.pendingNavigation = null;
    this.trustedOrigin = null;
    this.hostInstanceId = null;
  }

  private readonly handleMessage = (event: MessageEvent<unknown>): void => {
    if (this.disconnected || event.source !== this.parentWindow) return;

    const origin = this.trustedOrigin ?? event.origin;
    if (!this.trustedOrigin && !isAllowedParentOrigin(origin, this.guestWindow.location.hostname))
      return;
    if (event.origin !== origin) return;
    if (isUnsupportedHandshake(event.data)) {
      this.trustedOrigin = origin;
      this.versionError = incompatibleVersions();
      this.rejectPending(this.versionError);
      return;
    }
    let message: HostMessage;
    try {
      message = parseHostMessageEvent(event, {
        origin,
        source: this.parentWindow
      });
    } catch {
      this.failUnreadableResponse(event.data);
      return;
    }
    if (this.versionError && message.kind !== 'handshake') return;
    if (!this.trustedOrigin) {
      if (message.kind !== 'handshake') return;
      this.trustedOrigin = origin;
      this.hostInstanceId = message.payload.hostInstanceId;
    }
    this.acceptHostMessage(message);
  };

  private acceptHostMessage(message: HostMessage): void {
    if (message.kind === 'handshake') {
      this.versionError = null;
      if (message.payload.hostInstanceId !== this.hostInstanceId) {
        this.rejectPending(
          new SmartFillBridgeDisconnectedError('The extension host restarted.'),
          true
        );
        this.hostInstanceId = message.payload.hostInstanceId;
      }
      this.flushPending();
      return;
    }

    if (message.kind === 'event') {
      this.emit(message);
      return;
    }

    const pending = this.pending.get(message.requestId);
    if (!pending) return;
    try {
      const response = parseHostResponseFor(pending.request, message);
      this.finishPending(pending);
      if (response.ok) pending.resolve(response.result);
      else pending.reject(new SmartFillBridgeRequestError(response.error));
    } catch {
      // A response for a different command cannot settle the pending request.
    }
  }

  /**
   * An answer to a request this sidebar sent, in a current envelope but with a
   * result or error it cannot read, comes from an extension built against
   * another contract. Both sides share a bridge version, so the handshake cannot
   * tell; fail the request now instead of letting it time out.
   */
  private failUnreadableResponse(data: unknown): void {
    if (typeof data !== 'object' || data === null) return;
    const envelope = data as Record<string, unknown>;
    if (
      envelope.channel !== BRIDGE_CHANNEL ||
      envelope.version !== BRIDGE_VERSION ||
      envelope.direction !== 'host-to-guest' ||
      envelope.kind !== 'response' ||
      typeof envelope.requestId !== 'string'
    )
      return;
    const pending = this.pending.get(envelope.requestId);
    if (!pending?.sent || pending.request.command !== envelope.command) return;
    this.finishPending(pending);
    pending.reject(incompatibleVersions());
  }

  private emit(message: HostEvent): void {
    const listeners = this.listeners.get(message.event);
    if (!listeners) {
      if (message.event === 'sidebar.navigate') {
        this.pendingNavigation = message.payload;
      }
      return;
    }
    for (const listener of listeners) listener(message.payload, message.stamp);
  }

  private sendPending(pending: PendingRequest): void {
    if (pending.sent || !this.trustedOrigin || this.disconnected) return;
    try {
      this.parentWindow.postMessage(pending.request, this.trustedOrigin);
      pending.sent = true;
    } catch {
      pending.reject(new SmartFillBridgeDisconnectedError());
      this.finishPending(pending);
    }
  }

  private flushPending(): void {
    for (const pending of this.pending.values()) this.sendPending(pending);
  }

  private rejectPending(error: Error, sentOnly = false): void {
    for (const pending of [...this.pending.values()]) {
      if (sentOnly && !pending.sent) continue;
      this.finishPending(pending);
      pending.reject(error);
    }
  }

  private finishPending(pending: PendingRequest): void {
    this.guestWindow.clearTimeout(pending.timer);
    this.pending.delete(pending.request.requestId);
  }
}

export class SmartFillBridgeRequestError extends Error {
  readonly code: BridgeError['code'];
  readonly retryable: boolean;

  constructor(error: BridgeError) {
    super(error.message);
    this.name = 'SmartFillBridgeRequestError';
    this.code = error.code;
    this.retryable = error.retryable;
  }
}

export class SmartFillBridgeDisconnectedError extends Error {
  constructor(message = 'The Cara extension bridge disconnected.') {
    super(message);
    this.name = 'SmartFillBridgeDisconnectedError';
  }
}

function incompatibleVersions(): SmartFillBridgeRequestError {
  return new SmartFillBridgeRequestError({
    code: 'INVALID_REQUEST',
    message:
      'Smart Fill and the Cara extension use incompatible versions. Update the Cara extension and reload Smart Fill.',
    retryable: false
  });
}

/**
 * Staging and dev also accept unpacked builds, as their frame-ancestors do.
 * Every other host accepts only the store build, so a host added later starts
 * out pinned.
 */
function isAllowedParentOrigin(origin: string, hostname: string): boolean {
  if (hostname === 'smartfill.staging.getcara.ai' || hostname.endsWith('.dev.oysterinc.net'))
    return /^chrome-extension:\/\/[a-z0-9_-]{1,128}$/i.test(origin);
  return origin === STORE_EXTENSION_ORIGIN;
}

function isUnsupportedHandshake(value: unknown): boolean {
  if (typeof value !== 'object' || value === null || !('version' in value)) return false;
  if (
    !Number.isSafeInteger(value.version) ||
    (value.version as number) < 1 ||
    value.version === BRIDGE_VERSION
  )
    return false;
  try {
    // Validate the whole handshake shape without accepting its incompatible payload.
    parseHostHandshake({ ...value, version: BRIDGE_VERSION });
    return true;
  } catch {
    return false;
  }
}
